import { Injectable } from '@angular/core';
import { FilesService } from './files.service';
import { map, tap, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import _round from 'lodash/round';
import { isNull } from 'lodash-es';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { ISchedule, IScheduleIterationItem } from '../models/interfaces';
export const ROUNDVALUE = 4;

@Injectable({ providedIn: 'root' })
export class ForecastHelperService {
  constructor(private filesService: FilesService, private translocoService: TranslocoService) {}

  getIteration(slotSchedule: ISchedule, date: Date): IScheduleIterationItem | null {
    return (
      slotSchedule.iterations
        .filter(iteration => new Date(iteration.startDate) <= date)
        .sort((left, right) => {
          return differenceInSeconds(new Date(right.startDate), new Date(left.startDate));
        })[0] || null
    );
  }

  getCustomTimeWeights(file: File): Observable<number[][]> {
    return this.filesService.loadFileData(file).pipe(
      take(1),
      map(item =>
        (item as string)
          .split('\n')
          .map(row => row.split(';').map(item => parseFloat(item.replace(',', '.'))))
          .filter(item => item.filter(value => !isNaN(value))?.length),
      ),
      tap((dayWeights: number[][]) => {
        if (!dayWeights.length) {
          throw new Error(this.translocoService.translate('wfmModule.errors.forecastCustomTimeWeightsError'));
        }
      }),
    );
  }

  getTimeWeights(file: File): Observable<number[][]> {
    return this.filesService.loadFileData(file).pipe(
      take(1),
      map(item =>
        (item as string)
          .split('\n')
          .map(row => row.split(';').map(item => parseFloat(item.replace(',', '.'))))
          .filter(item => item.filter(value => !isNaN(value))?.length),
      ),
      tap((dayWeights: number[][]) => {
        if (dayWeights.some(value => value?.length !== 7)) {
          throw new Error(this.translocoService.translate('wfmModule.errors.forecastTimeWeightsError'));
        }
      }),
    );
  }

  getDayWeights(file: File): Observable<number[][]> {
    return this.filesService.loadFileData(file).pipe(
      map(item =>
        (item as string)
          .split('\n')
          .map(row => row.split(';').map(item => parseFloat(item.replace(',', '.'))))
          .filter(item => !item.some(value => isNaN(value))),
      ),
      tap((item: number[][]) => {
        // eslint-disable-next-line functional/immutable-data
        const dayWeights = item[0];
        if (!dayWeights.length || dayWeights.length > 31 || dayWeights.length < 28) {
          // eslint-disable-next-line functional/immutable-data
          throw new Error(this.translocoService.translate('wfmModule.errors.forecastMonthWeightsError'));
        }
      }),
    );
  }

  isNullDayWeightsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dayWeights = control.value;
      let valid = null;
      if (dayWeights && dayWeights.some((item: number | null) => isNull(item))) {
        // eslint-disable-next-line functional/immutable-data
        valid = {
          errorMessage: '',
        };
      }
      return valid;
    };
  }

  isNullTimeWeightsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const timeWeights = control.value;
      let valid = null;
      if (
        timeWeights &&
        timeWeights.some((item: number[]) => item.some((itemItem: null | number) => isNull(itemItem)))
      ) {
        valid = {
          errorMessage: '',
        };
      }
      return valid;
    };
  }

  dayWeightsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dayWeights = control.value;
      let valid = null;
      if (dayWeights) {
        const percentage = dayWeights.reduce((item: number, current: number) => {
          return item + current;
        }, 0);
        const roundPercentage = _round(percentage, ROUNDVALUE);
        if (roundPercentage !== 1) {
          // eslint-disable-next-line functional/immutable-data
          valid = {
            errorMessage: this.translocoService.translate('wfmModule.errors.sumNotEqualOneCurrent', {
              currentValue: _round(roundPercentage, ROUNDVALUE),
            }),
          };
        }
      }
      return valid;
    };
  }

  timeWeightsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const dayWeights = control.value;
      let valid = null;
      if (dayWeights) {
        let notValidDay = 0;
        const percentage = dayWeights.reduce(
          (item: number[], current: number[]) => {
            return current.map((value, index) => {
              return item[index] + value;
            });
          },
          [0, 0, 0, 0, 0, 0, 0],
        );
        let roundItemPercentage = 0;
        if (
          percentage.some((item: number, index: number) => {
            notValidDay = index + 1;
            roundItemPercentage = _round(item, ROUNDVALUE);
            return roundItemPercentage !== 1;
          })
        ) {
          valid = {
            errorMessage: this.translocoService.translate('wfmModule.errors.forecastWeekWeightsError', {
              entity: notValidDay,
              addEntity: this.translocoService
                .translate('wfmModule.errors.sumNotEqualOneCurrent', {
                  currentValue: roundItemPercentage,
                })
                .toLowerCase(),
            }),
          };
        }
      }
      return valid;
    };
  }

  transpose(matrix: number[][]): number[][] {
    return Object.keys(matrix[0]).map(colNumber => {
      return matrix.map((rowNumber: number[]) => rowNumber[colNumber]);
    });
  }
}
